// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { ReactComponent as CheckIcon } from "../assets/icons/check.svg";
import { ReactComponent as CalendarIcon } from "../assets/icons/calendar.svg";
import { Typography, message, Drawer } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { UpgradeModal } from "../components/upgrade";
import { useIsPremium } from "../hooks/useIsPremium";
import { FREE_PLAN_LIMITS } from "../config/upgrade";
import { useAppSelector } from "../store/hooks";
import { monthYearText } from "../utils/dateUtils";
import { APP_ROUTES } from "../utils/appRoutes";
import { colors } from "../styles/colors";
import { GoalRoadmap } from "../components/dream/GoalRoadmap";
import { FinanceReport } from "../components/dream/FinanceReport";
import MilestoneNotes from "../components/notes/MilestoneNotes";
import { coerceNotes, cleanNotesForSave } from "../components/notes/noteTypes";

function daysRemainingTo(month, year) {
  if (!month || !year) return null;
  const end = new Date(Number(year), Number(month), 0);
  const today = new Date();
  const ms =
    end.getTime() -
    new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function DreamDetail() {
  const { id } = useParams();
  const goalAttempt = useRef<Record<string, unknown> | null>(null);
  const milestoneAttempt = useRef<{ goalId: string; entries: Array<{ title: string; description: string; date?: string; requestId: string }> } | null>(null);
  const navigate = useNavigate();
  const [dream, setDream] = useState(null);
  const [goals, setGoals] = useState([]);
  const [milestonesByGoal, setMilestonesByGoal] = useState({});
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently } = useAuth0();
  const isPremium = useIsPremium();
  const dreamCards = useAppSelector((s) => s.dashboard.dreamCards);
  // Dream is locked if user is free-plan and has more dreams than the free limit
  // — only the first dream (index 0) remains editable after downgrade
  const isDreamLocked = !isPremium && dreamCards.find((dream) => dream.id === id)?.freeEditable === false;

  // Redirect to dashboard if a locked dream is accessed directly via URL
  useEffect(() => {
    if (!loading && isDreamLocked) {
      navigate(APP_ROUTES.dashboard, { replace: true });
    }
  }, [loading, isDreamLocked, navigate]);

  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState<"goal_limit" | "milestone_limit" | "dream_limit" | "reorder" | "duplicate">("goal_limit");
  const [showProgressPopup, setShowProgressPopup] = useState(false);
  const [viewTab, setViewTab] = useState<"goals" | "roadmap" | "finance">("goals");
  const [goalsLayout, setGoalsLayout] = useState<"horizontal" | "stacked">("horizontal");
  const [reorderMode, setReorderMode] = useState(false);
  const [dragGoalId, setDragGoalId] = useState<string | null>(null);
  const [dragOverGoalId, setDragOverGoalId] = useState<string | null>(null);
  const [dragMs, setDragMs] = useState<{ goalId: string; id: string } | null>(null);
  const [dragOverMs, setDragOverMs] = useState<{ goalId: string; id: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const startEditTitle = () => {
    setTitleValue(dream?.title || "");
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.select(), 0);
  };

  const saveTitle = async () => {
    const trimmed = titleValue.trim();
    if (!trimmed || trimmed === dream?.title) { setEditingTitle(false); return; }
    setSavingTitle(true);
    try {
      const updated = await request(API_URLS.dream(id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        body: { title: trimmed },
      });
      setDream((prev) => ({ ...prev, title: updated?.title ?? trimmed }));
      message.success("Dream title updated");
    } catch (e) {
      message.error(e?.message || "Failed to update title");
    } finally {
      setSavingTitle(false);
      setEditingTitle(false);
    }
  };
  const [noteDrawer, setNoteDrawer] = useState<{
    open: boolean;
    goal: any | null;
    milestone: any | null;
    notes: any[];
    saving: boolean;
  }>({ open: false, goal: null, milestone: null, notes: [], saving: false });

  const [taskDrawer, setTaskDrawer] = useState<{
    open: boolean;
    goal: any | null;
    milestone: any | null;
    tasks: { id: string; text: string; completed: boolean }[];
    newTaskText: string;
    saving: boolean;
    dragId: string | null;
    dragOverId: string | null;
  }>({ open: false, goal: null, milestone: null, tasks: [], newTaskText: "", saving: false, dragId: null, dragOverId: null });

  const nowDate = new Date();
  const [goalDrawer, setGoalDrawer] = useState({
    open: false,
    saving: false,
    confirmDelete: false,
    editingGoalId: null as string | null,
    title: "",
    description: "",
    startMonth: nowDate.getMonth() + 1,
    startYear: nowDate.getFullYear(),
    endMonth: nowDate.getMonth() + 1,
    endYear: nowDate.getFullYear(),
  });

  const [milestoneDrawer, setMilestoneDrawer] = useState({
    open: false,
    saving: false,
    confirmDelete: false,
    editingMilestoneId: null as string | null,
    goalId: null as string | null,
    goalTitle: "",
    title: "",
    description: "",
    date: "",
    entries: [] as { title: string; description: string; date: string }[],
  });

  const emptyMilestoneEntry = () => ({ title: "", description: "", date: "" });

  const handleDelete = () => setShowDeleteConfirm(true);

  const openGoalDrawer = () => {
    if (goalDrawer.saving) return;
    goalAttempt.current = null;
    const sm = dream?.startMonth || (new Date().getMonth() + 1);
    const sy = dream?.startYear || new Date().getFullYear();
    const em = dream?.targetMonth || sm;
    const ey = dream?.targetYear || sy;
    setGoalDrawer({ open: true, saving: false, confirmDelete: false, editingGoalId: null, title: "", description: "", startMonth: sm, startYear: sy, endMonth: em, endYear: ey });
  };

  const openEditGoalDrawer = (goal: any) => {
    setGoalDrawer({
      open: true,
      saving: false,
      confirmDelete: false,
      editingGoalId: goal.id,
      title: goal.title || "",
      description: goal.description || "",
      startMonth: goal.startMonth || dream?.startMonth || (new Date().getMonth() + 1),
      startYear: goal.startYear || dream?.startYear || new Date().getFullYear(),
      endMonth: goal.endMonth || dream?.targetMonth || (new Date().getMonth() + 1),
      endYear: goal.endYear || dream?.targetYear || new Date().getFullYear(),
    });
  };

  const saveGoal = async () => {
    if (goalDrawer.saving || !goalDrawer.title.trim()) return;
    const startIdx = goalDrawer.startYear * 12 + goalDrawer.startMonth;
    const endIdx = goalDrawer.endYear * 12 + goalDrawer.endMonth;
    if (startIdx > endIdx) {
      message.error("Start month cannot be after end month");
      return;
    }
    setGoalDrawer((p) => ({ ...p, saving: true }));
    const body = {
      title: goalDrawer.title.trim(),
      description: goalDrawer.description.trim(),
      startMonth: goalDrawer.startMonth,
      startYear: goalDrawer.startYear,
      endMonth: goalDrawer.endMonth,
      endYear: goalDrawer.endYear,
    };
    try {
      if (goalDrawer.editingGoalId) {
        const updated = await request(API_URLS.goal(id, goalDrawer.editingGoalId), {
          method: "PUT",
          auth: true,
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
          body,
        });
        setGoals((prev) => prev.map((g) => g.id === goalDrawer.editingGoalId ? { ...g, ...updated } : g));
        message.success("Goal updated");
      } else {
        goalAttempt.current ??= { ...body, requestId: crypto.randomUUID() };
        const created = await request(API_URLS.goals(id), {
          method: "POST",
          auth: true,
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
          body: goalAttempt.current,
        });
        setGoals((prev) => [...prev.filter((goal) => goal.id !== created.id), created]);
        setMilestonesByGoal((prev) => ({ ...prev, [created.id]: [] }));
        message.success("Goal added");
      }
      setGoalDrawer((p) => ({ ...p, open: false, saving: false }));
    } catch (e: any) {
      if (e?.httpStatus === 400) goalAttempt.current = null;
      message.error(goalAttempt.current ? "Retry will finish the original submitted goal without adding a duplicate." : e?.message || "Failed to save goal");
      setGoalDrawer((p) => ({ ...p, saving: false }));
    }
  };

  const openMilestoneDrawer = (goal?: any) => {
    if (milestoneDrawer.saving) return;
    milestoneAttempt.current = null;
    const targetGoal = goal || sortedGoals[0];
    setMilestoneDrawer({
      open: true,
      saving: false,
      confirmDelete: false,
      editingMilestoneId: null,
      goalId: targetGoal?.id ?? null,
      goalTitle: targetGoal?.title ?? "",
      title: "",
      description: "",
      date: "",
      entries: [emptyMilestoneEntry()],
    });
  };

  const openEditMilestoneDrawer = (goal: any, milestone: any) => {
    setMilestoneDrawer({
      open: true,
      saving: false,
      confirmDelete: false,
      editingMilestoneId: milestone.id,
      goalId: goal.id,
      goalTitle: goal.title,
      title: milestone.title || "",
      description: milestone.description || "",
      date: milestone.date || "",
      entries: [],
    });
  };

  const saveMilestone = async () => {
    if (milestoneDrawer.saving) return;
    const goalId = milestoneDrawer.goalId;
    if (!goalId) {
      message.error("Please select a goal");
      return;
    }
    // ── Edit mode: single milestone update ──
    if (milestoneDrawer.editingMilestoneId) {
      if (!milestoneDrawer.title.trim()) return;
      setMilestoneDrawer((p) => ({ ...p, saving: true }));
      try {
        const updated = await request(API_URLS.milestone(id, goalId, milestoneDrawer.editingMilestoneId), {
          method: "PUT",
          auth: true,
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
          body: {
            title: milestoneDrawer.title.trim(),
            description: milestoneDrawer.description.trim(),
            date: milestoneDrawer.date || "",
          },
        });
        setMilestonesByGoal((prev) => ({
          ...prev,
          [goalId]: (prev[goalId] || []).map((m) =>
            m.id === milestoneDrawer.editingMilestoneId ? { ...m, ...updated } : m
          ),
        }));
        message.success("Milestone updated");
        setMilestoneDrawer((p) => ({ ...p, open: false, saving: false }));
      } catch (e: any) {
        message.error(e?.message || "Failed to update milestone");
        setMilestoneDrawer((p) => ({ ...p, saving: false }));
      }
      return;
    }
    // ── Add mode: create one or more milestones for the selected goal ──
    const validEntries = (milestoneDrawer.entries || []).filter((e) => e.title.trim());
    if (!validEntries.length) {
      message.error("Add at least one milestone title");
      return;
    }
    setMilestoneDrawer((p) => ({ ...p, saving: true }));
    milestoneAttempt.current ??= { goalId, entries: validEntries.map((entry) => ({
      title: entry.title.trim(), description: entry.description.trim(), date: entry.date || undefined, requestId: crypto.randomUUID(),
    })) };
    const attempt = milestoneAttempt.current;
    try {
      const createdList: any[] = [];
      for (const entry of attempt.entries) {
        const created = await request(API_URLS.milestones(id, attempt.goalId), {
          method: "POST",
          auth: true,
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
          body: entry,
        });
        createdList.push(created);
      }
      const savedIds = new Set(createdList.map((milestone) => milestone.id));
      setMilestonesByGoal((prev) => ({
        ...prev,
        [attempt.goalId]: [...(prev[attempt.goalId] || []).filter((milestone) => !savedIds.has(milestone.id)), ...createdList],
      }));
      message.success(createdList.length > 1 ? `${createdList.length} milestones added` : "Milestone added");
      setMilestoneDrawer((p) => ({ ...p, open: false, saving: false }));
    } catch (e: any) {
      message.error("Retry will finish the original submitted milestones without duplicating saved entries. Refresh the dream to see any completed items.");
      setMilestoneDrawer((p) => ({ ...p, saving: false }));
    }
  };

  const deleteGoal = async () => {
    const goalId = goalDrawer.editingGoalId;
    if (!goalId) return;
    setGoalDrawer((p) => ({ ...p, saving: true }));
    try {
      await request(API_URLS.goal(id, goalId), {
        method: "DELETE",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      setMilestonesByGoal((prev) => {
        const next = { ...prev };
        delete next[goalId];
        return next;
      });
      message.success("Goal deleted");
      setGoalDrawer((p) => ({ ...p, open: false, saving: false }));
    } catch (e: any) {
      message.error(e?.message || "Failed to delete goal");
      setGoalDrawer((p) => ({ ...p, saving: false }));
    }
  };

  const confirmDeleteGoal = () => setGoalDrawer((p) => ({ ...p, confirmDelete: true }));

  const deleteMilestone = async () => {
    const goalId = milestoneDrawer.goalId;
    const msId = milestoneDrawer.editingMilestoneId;
    if (!goalId || !msId) return;
    setMilestoneDrawer((p) => ({ ...p, saving: true }));
    try {
      await request(API_URLS.milestone(id, goalId, msId), {
        method: "DELETE",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      setMilestonesByGoal((prev) => ({
        ...prev,
        [goalId]: (prev[goalId] || []).filter((m) => m.id !== msId),
      }));
      message.success("Milestone deleted");
      setMilestoneDrawer((p) => ({ ...p, open: false, saving: false }));
    } catch (e: any) {
      message.error(e?.message || "Failed to delete milestone");
      setMilestoneDrawer((p) => ({ ...p, saving: false }));
    }
  };

  const confirmDeleteMilestone = () => setMilestoneDrawer((p) => ({ ...p, confirmDelete: true }));

  const openNoteDrawer = (goal: any, milestone: any) => {
    setNoteDrawer({
      open: true,
      goal,
      milestone,
      notes: coerceNotes(milestone.notes),
      saving: false,
    });
  };

  const openTaskDrawer = (goal: any, milestone: any) => {
    setTaskDrawer({
      open: true,
      goal,
      milestone,
      tasks: Array.isArray(milestone.tasks) ? milestone.tasks : [],
      newTaskText: "",
      saving: false,
      dragId: null,
      dragOverId: null,
    });
  };

  // Persist tasks and reflect them on the milestone in state (used for live task edits)
  const saveTasksQuietly = async (goal: any, milestone: any, tasks: { id: string; text: string; completed: boolean }[]) => {
    if (!goal || !milestone) return;
    try {
      await request(API_URLS.milestone(id, goal.id, milestone.id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        body: { tasks },
      });
      setMilestonesByGoal((prev) => ({
        ...prev,
        [goal.id]: (prev[goal.id] || []).map((m) =>
          m.id === milestone.id ? { ...m, tasks } : m,
        ),
      }));
    } catch {
      // silent — a later change will retry
    }
  };

  // Apply a task-list change to the drawer state and persist it immediately
  const applyTaskChange = (updatedTasks: { id: string; text: string; completed: boolean }[], extra?: Partial<typeof taskDrawer>) => {
    setTaskDrawer((prev) => ({ ...prev, tasks: updatedTasks, ...(extra || {}) }));
    saveTasksQuietly(taskDrawer.goal, taskDrawer.milestone, updatedTasks);
  };

  const addTask = () => {
    const text = taskDrawer.newTaskText.trim();
    if (!text) return;
    const updated = [...taskDrawer.tasks, { id: `${Date.now()}-${Math.random()}`, text, completed: false }];
    applyTaskChange(updated, { newTaskText: "" });
  };

  const toggleTask = (taskId: string) => {
    const updated = taskDrawer.tasks.map((t) => (t.id === taskId ? { ...t, completed: !t.completed } : t));
    applyTaskChange(updated);
  };

  const removeTask = (taskId: string) => {
    const updated = taskDrawer.tasks.filter((t) => t.id !== taskId);
    applyTaskChange(updated);
  };

  const handleTaskDrop = (targetId: string) => {
    const draggedId = taskDrawer.dragId;
    if (!draggedId || draggedId === targetId) {
      setTaskDrawer((prev) => ({ ...prev, dragId: null, dragOverId: null }));
      return;
    }
    const ids = taskDrawer.tasks.map((t) => t.id);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) {
      setTaskDrawer((prev) => ({ ...prev, dragId: null, dragOverId: null }));
      return;
    }
    const next = [...taskDrawer.tasks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    applyTaskChange(next, { dragId: null, dragOverId: null });
  };

  const revealNoteDrawerSecret = async (noteId) => {
    if (!noteDrawer.goal || !noteDrawer.milestone) return null;
    try {
      const data = await request(API_URLS.milestoneNoteReveal(id, noteDrawer.goal.id, noteDrawer.milestone.id, noteId), {
        method: "POST",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      return data?.content ?? "";
    } catch (e: any) {
      message.error(e?.message || "Failed to unlock");
      return null;
    }
  };

  // Persist notes and reflect them on the milestone in state (auto-save on every change)
  const saveNotesQuietly = async (goal: any, milestone: any, notes: any[]) => {
    if (!goal || !milestone) return;
    try {
      await request(API_URLS.milestone(id, goal.id, milestone.id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        body: { notes: cleanNotesForSave(notes) },
      });
      setMilestonesByGoal((prev) => ({
        ...prev,
        [goal.id]: (prev[goal.id] || []).map((m) =>
          m.id === milestone.id ? { ...m, notes, hasNote: (notes || []).length > 0 } : m,
        ),
      }));
    } catch (e: any) {
      message.error(e?.message || "Failed to save note");
    }
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await request(API_URLS.dream(id), {
        method: "DELETE",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      message.success("Dream deleted");
      navigate(APP_ROUTES.myDreams);
    } catch (error) {
      message.error(error.message || "Failed to delete dream");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const dreamData = await request(API_URLS.dream(id), {
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      setDream(dreamData);
      const goalList = Array.isArray(dreamData?.goals) ? dreamData.goals : [];
      setGoals(goalList);
      const milestonesMap = {};
      for (const goal of goalList) {
        milestonesMap[goal.id] = Array.isArray(goal?.milestones) ? goal.milestones : [];
      }
      setMilestonesByGoal(milestonesMap);
    } catch (error) {
      message.error(error.message || "Failed to load dream");
    } finally {
      setLoading(false);
    }
  };

  const toggleMilestoneCompleted = async (goal, milestone) => {
    try {
      const updated = await request(
        API_URLS.milestone(id, goal.id, milestone.id),
        {
          method: "PUT",
          body: { completed: !milestone.completed },
          auth: true,
          getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        },
      );
      setMilestonesByGoal((prev) => ({
        ...prev,
        [goal.id]: (prev[goal.id] || []).map((item) =>
          item.id === milestone.id
            ? { ...updated, hasNote: coerceNotes(updated.notes).length > 0 }
            : item,
        ),
      }));
    } catch (error) {
      message.error(error.message || "Failed to update milestone");
    }
  };

  const getSortedMilestones = (goalId) => {
    const arr = (milestonesByGoal[goalId] || []).slice();
    const allHaveOrder = arr.length > 0 && arr.every((m) => typeof m.orderIndex === "number");
    if (allHaveOrder) return arr.sort((a, b) => a.orderIndex - b.orderIndex);
    return arr.sort((a, b) => {
      const dateA = a.date && !Number.isNaN(Date.parse(a.date)) ? new Date(a.date).getTime() : Number.POSITIVE_INFINITY;
      const dateB = b.date && !Number.isNaN(Date.parse(b.date)) ? new Date(b.date).getTime() : Number.POSITIVE_INFINITY;
      return dateA - dateB;
    });
  };

  const duplicateGoal = async (goal) => {
    if (isDreamLocked) { setUpgradeTrigger("dream_limit"); setShowUpgrade(true); return; }
    if (!isPremium) { setUpgradeTrigger("duplicate"); setShowUpgrade(true); return; }
    try {
      await request(API_URLS.goalDuplicate(id, goal.id), {
        method: "POST",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      await load();
      message.success("Goal duplicated");
    } catch (e: any) {
      message.error(e?.message || "Failed to duplicate goal");
    }
  };

  const duplicateMilestone = async (goal, milestone) => {
    if (isDreamLocked) { setUpgradeTrigger("dream_limit"); setShowUpgrade(true); return; }
    if (!isPremium) { setUpgradeTrigger("duplicate"); setShowUpgrade(true); return; }
    try {
      await request(API_URLS.milestoneDuplicate(id, goal.id, milestone.id), {
        method: "POST",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
      });
      await load();
      message.success("Milestone duplicated");
    } catch (e: any) {
      message.error(e?.message || "Failed to duplicate milestone");
    }
  };

  const persistGoalOrder = async (orderedIds) => {
    try {
      await request(API_URLS.goalsReorder(id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        body: { orderedIds },
      });
    } catch (e: any) {
      message.error(e?.message || "Failed to save goal order");
      load();
    }
  };

  const persistMilestoneOrder = async (goalId, orderedIds) => {
    try {
      await request(API_URLS.milestonesReorder(id, goalId), {
        method: "PUT",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "on" }),
        body: { orderedIds },
      });
    } catch (e: any) {
      message.error(e?.message || "Failed to save milestone order");
      load();
    }
  };

  const handleGoalDrop = (targetGoalId) => {
    const dragged = dragGoalId;
    setDragGoalId(null);
    setDragOverGoalId(null);
    if (!dragged || dragged === targetGoalId) return;
    const current = sortedGoals.map((g) => g.id);
    const from = current.indexOf(dragged);
    const to = current.indexOf(targetGoalId);
    if (from === -1 || to === -1) return;
    const next = [...current];
    next.splice(from, 1);
    next.splice(to, 0, dragged);
    setGoals((prev) => prev.map((g) => ({ ...g, orderIndex: next.indexOf(g.id) })));
    persistGoalOrder(next);
  };

  const handleMilestoneDrop = (goalId, targetId) => {
    const dragged = dragMs;
    setDragMs(null);
    setDragOverMs(null);
    if (!dragged || dragged.goalId !== goalId || dragged.id === targetId) return;
    const ids = getSortedMilestones(goalId).map((m) => m.id);
    const from = ids.indexOf(dragged.id);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, dragged.id);
    setMilestonesByGoal((prev) => ({
      ...prev,
      [goalId]: (prev[goalId] || []).map((m) => ({ ...m, orderIndex: next.indexOf(m.id) })),
    }));
    persistMilestoneOrder(goalId, next);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sortedGoals = useMemo(() => {
    const toIdx = (goal) =>
      Number(goal?.endYear || 0) * 12 + (Number(goal?.endMonth || 1) - 1);
    const allHaveOrder = goals.length > 0 && goals.every((g) => typeof g.orderIndex === "number");
    return [...goals]
      .sort((a, b) => (allHaveOrder ? a.orderIndex - b.orderIndex : toIdx(a) - toIdx(b)))
      .map((goal) => {
        const ms = milestonesByGoal[goal.id] || [];
        const allDone = ms.length > 0 && ms.every((m) => !!m.completed);
        return allDone ? { ...goal, completed: true } : goal;
      });
  }, [goals, milestonesByGoal]);

  const progressCounts = useMemo(() => {
    const totalGoals = goals.length;
    const completedGoals = goals.filter((goal) => !!goal.completed).length;
    const allMilestoneArrays = Object.values(milestonesByGoal || {});
    const totalMilestones = allMilestoneArrays.reduce(
      (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
      0,
    );
    const completedMilestones = allMilestoneArrays.reduce(
      (sum, arr) =>
        sum +
        (Array.isArray(arr) ? arr.filter((milestone) => !!milestone.completed).length : 0),
      0,
    );
    const goalsPercent = totalGoals
      ? Math.round((completedGoals / totalGoals) * 100)
      : 0;
    const milestonesPercent = totalMilestones
      ? Math.round((completedMilestones / totalMilestones) * 100)
      : 0;
    return {
      totalGoals,
      completedGoals,
      totalMilestones,
      completedMilestones,
      goalsPercent,
      milestonesPercent,
    };
  }, [goals, milestonesByGoal]);


  if (!dream && loading) return <div />;
  if (!dream)
    return <Typography.Text type="danger">Dream not found</Typography.Text>;

  const daysRemaining = daysRemainingTo(dream?.targetMonth, dream?.targetYear);
  const atGoalLimit = !isPremium && goals.length >= FREE_PLAN_LIMITS.goalsPerDream;

  const openGoalDrawerOrGate = () => {
    if (isDreamLocked) {
      setUpgradeTrigger("dream_limit");
      setShowUpgrade(true);
      return;
    }
    if (atGoalLimit) {
      setUpgradeTrigger("goal_limit");
      setShowUpgrade(true);
    } else {
      openGoalDrawer();
    }
  };

  const openMilestoneDrawerOrGate = () => {
    if (isDreamLocked) {
      setUpgradeTrigger("dream_limit");
      setShowUpgrade(true);
      return;
    }
    if (!sortedGoals.length) {
      message.info("Add a goal first, then you can add milestones to it");
      return;
    }
    openMilestoneDrawer();
  };

  const Gauge = ({ percent, color = "#1677FF", compact = false }) => {
    const clampedPercent = Math.max(0, Math.min(100, Number(percent) || 0));
    const radius    = compact ? 50 : 70;
    const centerX   = compact ? 65 : 90;
    const centerY   = compact ? 64 : 92;
    const svgW      = compact ? 130 : 180;
    const svgH      = compact ? 78 : 110;
    const containerH = compact ? 88 : 120;
    const circumference = Math.PI * radius;
    const dashLength = (clampedPercent / 100) * circumference;
    const angleDeg  = 180 - (clampedPercent / 100) * 180;
    const angleRad  = (angleDeg * Math.PI) / 180;
    const dotX = centerX + radius * Math.cos(angleRad);
    const dotY = centerY - radius * Math.sin(angleRad);
    return (
      <div style={{ position: "relative", width: svgW, height: containerH }}>
        <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: "block" }}>
          <path
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
            fill="none" stroke="#E7EBF0" strokeWidth="12" strokeLinecap="round"
          />
          <path
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
            fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={`${dashLength} ${circumference}`}
          />
          <circle cx={dotX} cy={dotY} r={compact ? 5 : 7} fill={color} />
        </svg>
        <div
          style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column",
            transform: `translateY(${compact ? 4 : 6}px)`,
          }}
        >
          <div style={{ fontSize: compact ? 16 : 22, fontWeight: 800, color: "#0b1b3a" }}>
            {clampedPercent}%
          </div>
          <div style={{ fontSize: compact ? 10 : 11, color: "rgba(0,0,0,0.55)" }}>
            Completed
          </div>
        </div>
      </div>
    );
  };

  const CircleToggle = ({ checked, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={checked}
      style={{
        appearance: "none",
        width: 22,
        height: 22,
        borderRadius: 999,
        border: checked ? "1px solid #1677FF" : "1px solid rgba(0,0,0,0.18)",
        background: checked ? "#1677FF" : "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      {checked ? <CheckIcon width="18" height="18" /> : null}
    </button>
  );

  const DatePill = ({ date }) => {
    let label = "No date";
    if (date && !Number.isNaN(Date.parse(date))) {
      const d = new Date(date);
      label = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    }
    return (
      <div
        style={{
          flex: "0 0 auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 10px",
          borderRadius: 999,
          background: "#EFF6FF",
          color: colors.brandPrimary,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          border: "1px solid rgba(0,136,255,0.15)",
        }}
      >
        <CalendarIcon width="12" height="12" />
        {label}
      </div>
    );
  };

  const _unusedProgressSummary = null; // removed — details now in ••• popup
  const progressSummaryPopup = (
    <div
      style={{
        position: "absolute",
        right: 0,
        top: "calc(100% + 8px)",
        zIndex: 200,
        width: 300,
      }}
    >
      <div
        style={{
          position: "relative",
          height: "100%",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: 18,
            boxShadow: "0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.07)",
          }}
        >
            <div style={{ fontWeight: 800, marginBottom: 12, color: "#0b1b3a" }}>
              Progress Summary
            </div>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Gauge percent={progressCounts.milestonesPercent} />
            </div>

            <div
              style={{
                fontSize: 11,
                letterSpacing: 1.0,
                color: "rgba(0,0,0,0.45)",
                fontWeight: 800,
                marginTop: 6,
                marginBottom: 8,
              }}
            >
              GOAL CONTEXT
            </div>

            <div
              style={{
                background: "#0B3A6B",
                borderRadius: 12,
                padding: 14,
                color: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "#34d399",
                  }}
                />
                <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 800 }}>
                  ACTIVE DREAM
                </div>
              </div>
              <div style={{ fontWeight: 900, marginBottom: 12 }}>{dream.title}</div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, opacity: 0.75 }}>Start date</div>
                  <div style={{ fontWeight: 800 }}>
                    {dream.startMonth && dream.startYear
                      ? monthYearText({
                          month: dream.startMonth,
                          year: dream.startYear,
                        })
                      : "—"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, opacity: 0.75 }}>Target</div>
                  <div style={{ fontWeight: 800 }}>
                    {monthYearText({
                      month: dream.targetMonth,
                      year: dream.targetYear,
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div
                style={{
                  background: "#F7F8FA",
                  borderRadius: 10,
                  padding: "10px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ color: "rgba(0,0,0,0.65)", fontWeight: 600 }}>
                  Total Milestones
                </div>
                <div style={{ fontWeight: 900, color: "#0b1b3a" }}>
                  {progressCounts.totalMilestones}
                </div>
              </div>
              <div
                style={{
                  background: "#F7F8FA",
                  borderRadius: 10,
                  padding: "10px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ color: "rgba(0,0,0,0.65)", fontWeight: 600 }}>
                  Days Remaining
                </div>
                <div style={{ fontWeight: 900, color: "#0b1b3a" }}>
                  {daysRemaining == null ? "—" : daysRemaining}
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ paddingTop: 10 }} className="min-w-0">
      <div style={{ marginBottom: showDeleteConfirm ? 0 : 10 }}>
        <button
          type="button"
          onClick={() => navigate(APP_ROUTES.myDreams)}
          style={{
            appearance: "none",
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            color: "#1677FF",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span style={{ marginRight: 6 }}>←</span> My Dreams
        </button>
      </div>


      {isDreamLocked && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3"
          style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <rect x="2" y="6.5" width="12" height="8" rx="1.5" stroke="#F97316" strokeWidth="1.3"/>
            <path d="M5 6.5V4.5a3 3 0 1 1 6 0v2" stroke="#F97316" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <p className="m-0 flex-1 text-[12px]" style={{ color: "#C2410C" }}>
            <span className="font-semibold">Read-only.</span> This dream is locked on your free plan. Upgrade to edit it.
          </p>
          <button
            type="button"
            onClick={() => { setUpgradeTrigger("dream_limit"); setShowUpgrade(true); }}
            className="flex-shrink-0 rounded-lg border-0 px-3 py-1.5 text-[11px] font-bold text-white"
            style={{ background: "#F97316", cursor: "pointer" }}
          >
            Upgrade
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-white px-4 py-4 shadow-sm md:px-5" style={{ border: `1px solid ${colors.borderLight}`, marginBottom: 16 }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingTitle ? (
              <input
                ref={titleInputRef}
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                disabled={savingTitle}
                style={{
                  width: "100%",
                  fontSize: 22,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  border: "none",
                  borderBottom: `2px solid ${dream.color || colors.brandPrimary}`,
                  outline: "none",
                  background: "transparent",
                  padding: "2px 0",
                  fontFamily: "inherit",
                }}
              />
            ) : (
              <h1
                className="m-0 text-[18px] font-bold md:text-[22px]"
                style={{ color: colors.textPrimary, cursor: isDreamLocked ? "default" : "text" }}
                onClick={isDreamLocked ? undefined : startEditTitle}
                title={isDreamLocked ? undefined : "Click to edit"}
              >
                {dream.title}
              </h1>
            )}
            <div className="mt-1.5 flex flex-col gap-1">
              {dream.category && (
                <span
                  className="inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white"
                  style={{ background: dream.color || colors.brandPrimary }}
                >
                  {dream.category}
                </span>
              )}
              {dream.startMonth && dream.startYear && (
                <span className="text-[12px] text-slate-400">
                  {monthYearText({ month: dream.startMonth, year: dream.startYear })}
                  {" → "}
                  {monthYearText({ month: dream.targetMonth, year: dream.targetYear })}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Gauge percent={progressCounts.milestonesPercent} color={dream.color || colors.brandPrimary} compact />
          </div>
        </div>
      </div>

      <div style={{ marginTop: 6, marginBottom: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#0b1b3a" }}>
          Milestones &amp; Action Plan
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
          Descriptions are automatically generated from your dream description
        </div>
      </div>

      {/* ── View tabs ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginTop: 16,
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 8,
          borderBottom: "2px solid #e7ebf0",
        }}
      >
        <div style={{ display: "flex", gap: 0 }}>
          {(["goals", "roadmap", "finance"] as const).map((tab) => {
            const active = viewTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setViewTab(tab)}
                style={{
                  border: "none",
                  borderBottom: active ? `2px solid ${colors.brandPrimary}` : "2px solid transparent",
                  background: "transparent",
                  padding: "8px 20px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  color: active ? colors.brandPrimary : "rgba(0,0,0,0.4)",
                  marginBottom: -2,
                  transition: "color 0.15s, border-color 0.15s",
                }}
              >
                {tab === "goals" ? "Goals & Milestones" : tab === "roadmap" ? "Roadmap" : "Finance"}
              </button>
            );
          })}
        </div>
        {viewTab === "goals" && (
          <div className="flex" style={{ alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            {!isDreamLocked && (
              <>
                <button
                  type="button"
                  title="Add goal"
                  onClick={openGoalDrawerOrGate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 10,
                    border: `1px solid ${colors.brandPrimary}`,
                    background: "#fff",
                    color: colors.brandPrimary,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/><line x1="10" y1="5" x2="10" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="5" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  Goal
                </button>
                <button
                  type="button"
                  title={sortedGoals.length ? "Add milestones" : "Add a goal first"}
                  onClick={openMilestoneDrawerOrGate}
                  disabled={!sortedGoals.length}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 10,
                    border: `1px solid ${sortedGoals.length ? colors.brandPrimary : "#CBD5E1"}`,
                    background: "#fff",
                    color: sortedGoals.length ? colors.brandPrimary : "#94A3B8",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: sortedGoals.length ? "pointer" : "not-allowed",
                    transition: "all 0.15s",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/><line x1="10" y1="5" x2="10" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="5" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  Milestone
                </button>
              </>
            )}
            <div className="hidden sm:flex" style={{ alignItems: "center", gap: 8 }}>
            <button
              type="button"
              title={reorderMode ? "Done reordering" : "Reorder goals & milestones"}
              onClick={() => {
                if (!isPremium) { setUpgradeTrigger("reorder"); setShowUpgrade(true); return; }
                setReorderMode((v) => !v);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 34,
                padding: "0 12px",
                borderRadius: 10,
                border: reorderMode ? "none" : "1px solid rgba(0,0,0,0.12)",
                background: reorderMode ? colors.brandPrimary : "#fff",
                color: reorderMode ? "#fff" : "rgba(0,0,0,0.55)",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="8 7 5 4 2 7" /><line x1="5" y1="4" x2="5" y2="14" />
                <polyline points="16 17 19 20 22 17" /><line x1="19" y1="20" x2="19" y2="10" />
              </svg>
              {reorderMode ? "Done" : "Reorder"}
            </button>
            <div
              style={{
                display: "inline-flex",
                gap: 2,
                background: "#F1F5F9",
                borderRadius: 10,
                padding: 3,
              }}
            >
            <button
              type="button"
              title="Side by side"
              onClick={() => setGoalsLayout("horizontal")}
              style={{
                width: 30,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: 7,
                cursor: "pointer",
                background: goalsLayout === "horizontal" ? colors.brandPrimary : "transparent",
                color: goalsLayout === "horizontal" ? "#ffffff" : "rgba(0,0,0,0.35)",
                transition: "background 0.15s, color 0.15s",
                boxShadow: goalsLayout === "horizontal" ? "0 1px 3px rgba(0,136,255,0.25)" : "none",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="2" width="3.5" height="10" rx="1" fill="currentColor" />
                <rect x="5.25" y="2" width="3.5" height="10" rx="1" fill="currentColor" />
                <rect x="9.5" y="2" width="3.5" height="10" rx="1" fill="currentColor" />
              </svg>
            </button>
            <button
              type="button"
              title="Stacked"
              onClick={() => setGoalsLayout("stacked")}
              style={{
                width: 30,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                borderRadius: 7,
                cursor: "pointer",
                background: goalsLayout === "stacked" ? colors.brandPrimary : "transparent",
                color: goalsLayout === "stacked" ? "#ffffff" : "rgba(0,0,0,0.35)",
                transition: "background 0.15s, color 0.15s",
                boxShadow: goalsLayout === "stacked" ? "0 1px 3px rgba(0,136,255,0.25)" : "none",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="2" width="12" height="2.5" rx="1" fill="currentColor" />
                <rect x="1" y="5.75" width="12" height="2.5" rx="1" fill="currentColor" />
                <rect x="1" y="9.5" width="12" height="2.5" rx="1" fill="currentColor" />
              </svg>
            </button>
            </div>
            </div>
          </div>
        )}
      </div>


      <div style={{ flex: 1, minWidth: 0 }}>
          {viewTab === "roadmap" ? (
            <GoalRoadmap goals={sortedGoals} dreamColor={dream?.color || "#8B5CF6"} />
          ) : viewTab === "finance" ? (
            <FinanceReport
              goals={sortedGoals}
              milestonesByGoal={milestonesByGoal}
              locked={!isPremium}
              onUpgrade={() => { setUpgradeTrigger("finance"); setShowUpgrade(true); }}
            />
          ) : (
          <div
            className={`goals-layout-container flex flex-col ${goalsLayout === "horizontal" ? "sm:flex-row sm:overflow-x-auto" : ""}`}
            style={{
              gap: 20,
              overscrollBehaviorX: "none",
              paddingBottom: 12,
            }}
          >
            {sortedGoals.map((goal) => {
                const milestones = getSortedMilestones(goal.id);

                const goalAllDone = milestones.length > 0 && milestones.every((m) => !!m.completed);

                return (
                  <div
                    key={goal.id}
                    className={goalsLayout === "horizontal" ? "w-full sm:w-[30%] sm:flex-none" : "w-full"}
                    draggable={reorderMode && !isDreamLocked}
                    onDragStart={reorderMode && !isDreamLocked ? (e) => { setDragGoalId(goal.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", goal.id); } : undefined}
                    onDragOver={reorderMode && !isDreamLocked ? (e) => { e.preventDefault(); if (dragGoalId && dragOverGoalId !== goal.id) setDragOverGoalId(goal.id); } : undefined}
                    onDrop={reorderMode && !isDreamLocked ? (e) => { e.preventDefault(); handleGoalDrop(goal.id); } : undefined}
                    onDragEnd={reorderMode ? () => { setDragGoalId(null); setDragOverGoalId(null); } : undefined}
                    style={{
                      minWidth: 0,
                      flex: goalsLayout === "horizontal" ? undefined : "1 1 auto",
                      opacity: dragGoalId === goal.id ? 0.5 : 1,
                      outline: reorderMode && dragGoalId && dragOverGoalId === goal.id && dragGoalId !== goal.id ? `2px dashed ${colors.brandPrimary}` : undefined,
                      outlineOffset: 6,
                      borderRadius: 14,
                      cursor: reorderMode && !isDreamLocked ? (dragGoalId === goal.id ? "grabbing" : "grab") : undefined,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 4,
                        marginBottom: 12,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {reorderMode && !isDreamLocked && (
                          <span title="Drag to reorder" style={{ display: "inline-flex", color: "rgba(0,0,0,0.3)", cursor: "grab", flexShrink: 0 }}>
                            <svg width="14" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="9" cy="6" r="1.7" /><circle cx="15" cy="6" r="1.7" />
                              <circle cx="9" cy="12" r="1.7" /><circle cx="15" cy="12" r="1.7" />
                              <circle cx="9" cy="18" r="1.7" /><circle cx="15" cy="18" r="1.7" />
                            </svg>
                          </span>
                        )}
                        <span
                          onClick={reorderMode || isDreamLocked ? undefined : (e) => { e.stopPropagation(); openEditGoalDrawer(goal); }}
                          title={reorderMode || isDreamLocked ? undefined : "Click to edit goal"}
                          style={{ fontSize: 18, fontWeight: 800, color: "#0b1b3a", cursor: reorderMode || isDreamLocked ? "default" : "pointer" }}
                        >
                          {goal.title}
                        </span>
                        {!isDreamLocked && !reorderMode && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); duplicateGoal(goal); }}
                            title="Duplicate goal & its milestones"
                            style={{
                              appearance: "none",
                              border: "1px solid rgba(0,0,0,0.1)",
                              borderRadius: 7,
                              background: "transparent",
                              width: 28,
                              height: 28,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "rgba(0,0,0,0.35)",
                              flexShrink: 0,
                              transition: "all 0.15s",
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="11" height="11" rx="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {(goal.startMonth || goal.endMonth) && (goal.startYear || goal.endYear) && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: colors.brandPrimary,
                              background: "#EFF6FF",
                              border: "1px solid rgba(0,136,255,0.15)",
                              borderRadius: 999,
                              padding: "2px 10px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {monthYearText({
                              month: goal.startMonth || goal.endMonth,
                              year: goal.startYear || goal.endYear,
                            })}
                          </span>
                        )}
                        {goalAllDone && (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#EFF6FF",
                            border: "1px solid rgba(0,136,255,0.3)",
                            borderRadius: 999,
                            padding: "2px 8px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: colors.brandPrimary,
                            flexShrink: 0,
                          }}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <polyline points="1.5,5 4,7.5 8.5,2" stroke={colors.brandPrimary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Completed
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                      }}
                    >
                      {milestones.length === 0 ? (
                        <div style={{ display: "flex", alignItems: "stretch", gap: 14 }}>
                          <div style={{ width: 22, flexShrink: 0 }} />
                          <div
                            style={{
                              flex: 1,
                              border: "1px dashed rgba(0,0,0,0.12)",
                              borderRadius: 10,
                              padding: "10px 14px",
                              fontSize: 12,
                              color: "rgba(0,0,0,0.35)",
                            }}
                          >
                            No milestones yet — use the Milestone button above to add some.
                          </div>
                        </div>
                      ) : (
                        milestones.map((milestone, idx) => {
                          const isLast = idx === milestones.length - 1;
                          const isFirst = idx === 0;
                          const timelineGapPx = 14;
                          const circleTopPadPx = 6;
                          const circleSizePx = 18;
                          const circleTopPx = circleTopPadPx;
                          const circleBottomPx = circleTopPadPx + circleSizePx;
                          const lineColor = "#E7EBF0";

                          return (
                            <div
                              key={milestone.id}
                              draggable={reorderMode && !isDreamLocked}
                              onDragStart={reorderMode && !isDreamLocked ? (e) => { e.stopPropagation(); setDragMs({ goalId: goal.id, id: milestone.id }); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", milestone.id); } : undefined}
                              onDragOver={reorderMode && !isDreamLocked ? (e) => { e.preventDefault(); e.stopPropagation(); if (dragMs && dragMs.goalId === goal.id && dragOverMs?.id !== milestone.id) setDragOverMs({ goalId: goal.id, id: milestone.id }); } : undefined}
                              onDrop={reorderMode && !isDreamLocked ? (e) => { e.preventDefault(); e.stopPropagation(); handleMilestoneDrop(goal.id, milestone.id); } : undefined}
                              onDragEnd={reorderMode ? (e) => { e.stopPropagation(); setDragMs(null); setDragOverMs(null); } : undefined}
                              style={{
                                display: "flex",
                                alignItems: "stretch",
                                gap: 14,
                                opacity: dragMs && dragMs.id === milestone.id ? 0.5 : 1,
                                outline: reorderMode && dragMs && dragMs.goalId === goal.id && dragOverMs?.id === milestone.id && dragMs.id !== milestone.id ? `2px dashed ${colors.brandPrimary}` : undefined,
                                outlineOffset: 4,
                                borderRadius: 12,
                                cursor: reorderMode && !isDreamLocked ? (dragMs?.id === milestone.id ? "grabbing" : "grab") : undefined,
                              }}
                            >
                              <div
                                style={{
                                  width: 22,
                                  position: "relative",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                {!isFirst ? (
                                  <div
                                    aria-hidden
                                    style={{
                                      position: "absolute",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      width: 2,
                                      top: -timelineGapPx,
                                      height: timelineGapPx + circleTopPx,
                                      background: lineColor,
                                      zIndex: 0,
                                    }}
                                  />
                                ) : null}
                                {!isLast ? (
                                  <div
                                    aria-hidden
                                    style={{
                                      position: "absolute",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      width: 2,
                                      top: circleBottomPx,
                                      bottom: -timelineGapPx,
                                      background: lineColor,
                                      zIndex: 0,
                                    }}
                                  />
                                ) : null}
                                <div style={{ paddingTop: circleTopPadPx, position: "relative", zIndex: 1 }}>
                                  <CircleToggle
                                    checked={!!milestone.completed}
                                    onToggle={() =>
                                      toggleMilestoneCompleted(goal, milestone)
                                    }
                                  />
                                </div>
                              </div>

                              <div
                                style={{
                                  flex: 1,
                                  background: "#fff",
                                  border: "1px solid rgba(0,0,0,0.06)",
                                  borderRadius: 12,
                                  padding: 14,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 12,
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      onClick={reorderMode || isDreamLocked ? undefined : (e) => { e.stopPropagation(); openEditMilestoneDrawer(goal, milestone); }}
                                      title={reorderMode || isDreamLocked ? undefined : "Click to edit milestone"}
                                      style={{
                                        fontWeight: 800,
                                        color: "#0b1b3a",
                                        cursor: reorderMode || isDreamLocked ? "default" : "pointer",
                                      }}
                                    >
                                      {milestone.title}
                                    </div>
                                    {milestone.date && (
                                      <div style={{ marginTop: 4 }}>
                                        <DatePill date={milestone.date} />
                                      </div>
                                    )}
                                    {milestone.description ? (
                                      <div
                                        style={{
                                          marginTop: 6,
                                          color: "rgba(0,0,0,0.55)",
                                          fontSize: 12,
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {milestone.description}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div style={{ display: "flex", alignItems: "flex-start", gap: 4, flexShrink: 0 }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {(() => {
                                      const tks = Array.isArray(milestone.tasks) ? milestone.tasks : [];
                                      const total = tks.length;
                                      const done = tks.filter((t) => t.completed).length;
                                      const allDone = total > 0 && done === total;
                                      const active = total > 0;
                                      const accent = dream?.color || "#1677FF";
                                      return (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); openTaskDrawer(goal, milestone); }}
                                          title={total ? `Tasks — ${done}/${total} done` : "Add tasks"}
                                          style={{
                                            position: "relative",
                                            appearance: "none",
                                            border: active ? `1px solid ${accent}40` : "1px solid rgba(0,0,0,0.1)",
                                            background: active ? `${accent}0f` : "transparent",
                                            borderRadius: 7,
                                            width: 28,
                                            height: 28,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            flexShrink: 0,
                                            transition: "all 0.15s",
                                          }}
                                        >
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={active ? accent : "rgba(0,0,0,0.35)"}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M9 11l3 3L22 4" />
                                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                          </svg>
                                          {total > 0 && (
                                            <span
                                              style={{
                                                position: "absolute",
                                                top: -10,
                                                right: -8,
                                                minWidth: 16,
                                                height: 16,
                                                padding: "0 4px",
                                                borderRadius: 999,
                                                background: allDone ? "#22c55e" : accent,
                                                color: "#fff",
                                                fontSize: 10,
                                                fontWeight: 800,
                                                lineHeight: "16px",
                                                textAlign: "center",
                                                border: "1.5px solid #fff",
                                                boxSizing: "border-box",
                                              }}
                                            >
                                              {done}/{total}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })()}
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openNoteDrawer(goal, milestone); }}
                                      title={milestone.hasNote ? "Edit note" : "Add note"}
                                      style={{
                                        position: "relative",
                                        appearance: "none",
                                        border: milestone.hasNote
                                          ? `1px solid ${dream?.color || "#1677FF"}40`
                                          : "1px solid rgba(0,0,0,0.1)",
                                        background: milestone.hasNote
                                          ? `${dream?.color || "#1677FF"}0f`
                                          : "transparent",
                                        borderRadius: 7,
                                        width: 28,
                                        height: 28,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        flexShrink: 0,
                                        transition: "all 0.15s",
                                      }}
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke={milestone.hasNote ? (dream?.color || "#1677FF") : "rgba(0,0,0,0.35)"}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                      </svg>
                                      {milestone.hasNote && (
                                        <span
                                          style={{
                                            position: "absolute",
                                            top: -3,
                                            right: -3,
                                            width: 8,
                                            height: 8,
                                            borderRadius: 999,
                                            background: dream?.color || "#1677FF",
                                            border: "1.5px solid #fff",
                                          }}
                                        />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); duplicateMilestone(goal, milestone); }}
                                      title="Duplicate milestone"
                                      style={{
                                        appearance: "none",
                                        border: "1px solid rgba(0,0,0,0.1)",
                                        borderRadius: 7,
                                        background: "transparent",
                                        width: 28,
                                        height: 28,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        color: "rgba(0,0,0,0.35)",
                                        flexShrink: 0,
                                        transition: "all 0.15s",
                                      }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="11" height="11" rx="2" />
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}

            {/* Empty state — no goals yet */}
            {sortedGoals.length === 0 && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  border: "2px dashed rgba(0,0,0,0.10)",
                  borderRadius: 20,
                  padding: "60px 24px",
                  textAlign: "center",
                }}
              >
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ opacity: 0.25 }}>
                  <circle cx="20" cy="20" r="17" stroke="#64748B" strokeWidth="2"/>
                  <circle cx="20" cy="20" r="10" stroke="#64748B" strokeWidth="2"/>
                  <circle cx="20" cy="20" r="3" fill="#64748B"/>
                </svg>
                <div>
                  <p style={{ fontWeight: 700, color: "#0b1b3a", margin: 0 }}>No goals yet</p>
                  <p style={{ fontSize: 13, color: "rgba(0,0,0,0.4)", margin: "4px 0 0" }}>Break your dream down into goals and milestones</p>
                </div>
                <button
                  type="button"
                  onClick={openGoalDrawerOrGate}
                  style={{
                    marginTop: 4,
                    padding: "9px 22px",
                    borderRadius: 10,
                    border: "none",
                    background: dream?.color || "#1677FF",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  + Add Goal
                </button>
              </div>
            )}
            </div>
          )}
      </div>

      <Drawer
        open={noteDrawer.open}
        onClose={() => setNoteDrawer((prev) => ({ ...prev, open: false }))}
        placement="right"
        width="min(640px, 92vw)"
        title={
          <div>
            <div style={{ fontWeight: 800, color: "#0b1b3a", fontSize: 15 }}>Note</div>
            {noteDrawer.milestone && (
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)", fontWeight: 400, marginTop: 2 }}>
                {noteDrawer.milestone.title}
              </div>
            )}
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => setNoteDrawer((prev) => ({ ...prev, open: false }))}
              style={{ appearance: "none", border: "none", background: colors.brandPrimary, color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              Done
            </button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          <MilestoneNotes
            notes={noteDrawer.notes}
            onChange={(notes) => { setNoteDrawer((prev) => ({ ...prev, notes })); saveNotesQuietly(noteDrawer.goal, noteDrawer.milestone, notes); }}
            isPremium={isPremium}
            onReveal={revealNoteDrawerSecret}
            onUpgrade={() => { setUpgradeTrigger("milestone_limit"); setShowUpgrade(true); }}
          />

        </div>
      </Drawer>

      {/* ── Tasks Drawer ── */}
      <Drawer
        open={taskDrawer.open}
        onClose={() => setTaskDrawer((prev) => ({ ...prev, open: false }))}
        placement="right"
        width={420}
        title={
          <div>
            <div style={{ fontWeight: 800, color: "#0b1b3a", fontSize: 15 }}>Tasks</div>
            {taskDrawer.milestone && (
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)", fontWeight: 400, marginTop: 2 }}>
                {taskDrawer.milestone.title}
              </div>
            )}
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
              {taskDrawer.tasks.length > 0
                ? `${taskDrawer.tasks.filter((t) => t.completed).length}/${taskDrawer.tasks.length} completed`
                : "No tasks yet"}
            </div>
            <button
              type="button"
              onClick={() => setTaskDrawer((prev) => ({ ...prev, open: false }))}
              style={{ appearance: "none", border: "none", background: colors.brandPrimary, color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              Done
            </button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Add task input */}
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={taskDrawer.newTaskText}
              onChange={(e) => setTaskDrawer((prev) => ({ ...prev, newTaskText: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
              placeholder="Add a task and press Enter…"
              autoFocus
              style={{ flex: 1, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 9, padding: "7px 11px", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
            />
            <button
              type="button"
              disabled={!taskDrawer.newTaskText.trim()}
              onClick={addTask}
              style={{ flexShrink: 0, appearance: "none", border: "none", background: colors.brandPrimary, color: "#fff", borderRadius: 9, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: taskDrawer.newTaskText.trim() ? "pointer" : "not-allowed", opacity: taskDrawer.newTaskText.trim() ? 1 : 0.45, transition: "opacity 0.15s" }}
            >
              Add
            </button>
          </div>

          {taskDrawer.tasks.length === 0 ? (
            <div style={{ border: "1px dashed rgba(0,0,0,0.12)", borderRadius: 10, padding: "14px", fontSize: 12, color: "rgba(0,0,0,0.4)", textAlign: "center" }}>
              No tasks yet. Add your first task above.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {taskDrawer.tasks.map((task) => {
                const isDragging = taskDrawer.dragId === task.id;
                const isOver = taskDrawer.dragOverId === task.id && taskDrawer.dragId !== task.id;
                return (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={() => setTaskDrawer((prev) => ({ ...prev, dragId: task.id }))}
                    onDragOver={(e) => { e.preventDefault(); if (taskDrawer.dragOverId !== task.id) setTaskDrawer((prev) => ({ ...prev, dragOverId: task.id })); }}
                    onDrop={(e) => { e.preventDefault(); handleTaskDrop(task.id); }}
                    onDragEnd={() => setTaskDrawer((prev) => ({ ...prev, dragId: null, dragOverId: null }))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      background: task.completed ? "#F8FAFC" : "#fff",
                      border: isOver ? `1px dashed ${colors.brandPrimary}` : "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 9,
                      padding: "8px 10px",
                      opacity: isDragging ? 0.5 : 1,
                      transition: "opacity 0.15s, border-color 0.15s",
                    }}
                  >
                    {/* Drag handle */}
                    <span title="Drag to reorder" style={{ flexShrink: 0, cursor: "grab", color: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center" }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                        <circle cx="3" cy="2" r="1" /><circle cx="9" cy="2" r="1" />
                        <circle cx="3" cy="6" r="1" /><circle cx="9" cy="6" r="1" />
                        <circle cx="3" cy="10" r="1" /><circle cx="9" cy="10" r="1" />
                      </svg>
                    </span>
                    {/* Checkbox */}
                    <button
                      type="button"
                      onClick={() => toggleTask(task.id)}
                      style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 4, border: task.completed ? `2px solid ${colors.brandPrimary}` : "2px solid rgba(0,0,0,0.2)", background: task.completed ? colors.brandPrimary : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, appearance: "none", transition: "all 0.15s" }}
                    >
                      {task.completed && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      )}
                    </button>
                    {/* Text */}
                    <span style={{ flex: 1, fontSize: 13, color: task.completed ? "rgba(0,0,0,0.35)" : "#0b1b3a", textDecoration: task.completed ? "line-through" : "none", lineHeight: 1.4 }}>
                      {task.text}
                    </span>
                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => removeTask(task.id)}
                      style={{ flexShrink: 0, appearance: "none", border: "none", background: "none", cursor: "pointer", color: "rgba(0,0,0,0.25)", padding: 2, display: "flex", alignItems: "center" }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Drawer>

      {/* ── Add Goal Drawer ── */}
      <Drawer
        open={goalDrawer.open}
        onClose={() => setGoalDrawer((p) => ({ ...p, open: false }))}
        placement="right"
        width={440}
        title={<div style={{ fontWeight: 800, color: "#0b1b3a", fontSize: 15 }}>{goalDrawer.editingGoalId ? "Edit Goal" : "Add Goal"}</div>}
        footer={
          goalDrawer.confirmDelete ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>Delete this goal?</div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", lineHeight: 1.5, marginBottom: 12 }}>
                This will permanently remove the goal and all of its milestones. This cannot be undone.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => setGoalDrawer((p) => ({ ...p, confirmDelete: false }))} disabled={goalDrawer.saving}
                  style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "transparent", fontSize: 13, cursor: goalDrawer.saving ? "not-allowed" : "pointer" }}>
                  Cancel
                </button>
                <button type="button" onClick={deleteGoal} disabled={goalDrawer.saving}
                  style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: goalDrawer.saving ? "not-allowed" : "pointer", opacity: goalDrawer.saving ? 0.6 : 1 }}>
                  {goalDrawer.saving ? "Deleting…" : "Yes, delete"}
                </button>
              </div>
            </div>
          ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div>
              {goalDrawer.editingGoalId && (
                <button type="button" onClick={confirmDeleteGoal} disabled={goalDrawer.saving}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid #fca5a5", background: "transparent", color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: goalDrawer.saving ? "not-allowed" : "pointer" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => setGoalDrawer((p) => ({ ...p, open: false }))}
                style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "transparent", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
              <button type="button" onClick={saveGoal} disabled={goalDrawer.saving || !goalDrawer.title.trim()}
                style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: colors.brandPrimary, color: "#fff", fontSize: 13, fontWeight: 700, cursor: goalDrawer.saving ? "not-allowed" : "pointer", opacity: goalDrawer.saving || !goalDrawer.title.trim() ? 0.6 : 1 }}>
                {goalDrawer.saving ? "Saving…" : goalDrawer.editingGoalId ? "Update Goal" : "Save Goal"}
              </button>
            </div>
          </div>
          )
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Goal title *</label>
            <input
              value={goalDrawer.title}
              onChange={(e) => setGoalDrawer((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Complete online course"
              autoFocus
              style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Description</label>
            <textarea
              value={goalDrawer.description}
              onChange={(e) => setGoalDrawer((p) => ({ ...p, description: e.target.value }))}
              placeholder="What does this goal involve?"
              rows={3}
              style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            />
          </div>
          {(() => {
            const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const dSm = dream?.startMonth || 1;
            const dSy = dream?.startYear || new Date().getFullYear();
            const dEm = dream?.targetMonth || 12;
            const dEy = dream?.targetYear || new Date().getFullYear();
            const dreamStartIdx = dSy * 12 + dSm;
            const dreamEndIdx = dEy * 12 + dEm;
            const monthOptions: { label: string; month: number; year: number }[] = [];
            for (let idx = dreamStartIdx; idx <= dreamEndIdx; idx++) {
              const y = Math.floor((idx - 1) / 12);
              const m = ((idx - 1) % 12) + 1;
              monthOptions.push({ label: `${MONTH_NAMES[m - 1]} ${y}`, month: m, year: y });
            }
            const toVal = (month: number, year: number) => `${year}-${month}`;
            const fromVal = (v: string) => { const [y, m] = v.split("-").map(Number); return { month: m, year: y }; };
            const startIdx = goalDrawer.startYear * 12 + goalDrawer.startMonth;
            const endIdx = goalDrawer.endYear * 12 + goalDrawer.endMonth;
            const orderError = startIdx > endIdx;
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Start month</label>
                  <select
                    value={toVal(goalDrawer.startMonth, goalDrawer.startYear)}
                    onChange={(e) => { const { month, year } = fromVal(e.target.value); setGoalDrawer((p) => ({ ...p, startMonth: month, startYear: year })); }}
                    style={{ width: "100%", border: `1px solid ${orderError ? "#ff4d4f" : "rgba(0,0,0,0.15)"}`, borderRadius: 8, padding: "7px 8px", fontSize: 13, outline: "none" }}
                  >
                    {monthOptions.map((o) => <option key={toVal(o.month, o.year)} value={toVal(o.month, o.year)}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>End month</label>
                  <select
                    value={toVal(goalDrawer.endMonth, goalDrawer.endYear)}
                    onChange={(e) => { const { month, year } = fromVal(e.target.value); setGoalDrawer((p) => ({ ...p, endMonth: month, endYear: year })); }}
                    style={{ width: "100%", border: `1px solid ${orderError ? "#ff4d4f" : "rgba(0,0,0,0.15)"}`, borderRadius: 8, padding: "7px 8px", fontSize: 13, outline: "none" }}
                  >
                    {monthOptions.map((o) => <option key={toVal(o.month, o.year)} value={toVal(o.month, o.year)}>{o.label}</option>)}
                  </select>
                </div>
                {orderError && (
                  <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "#ff4d4f", marginTop: -4 }}>
                    Start month cannot be after end month.
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </Drawer>

      {/* ── Add Milestone Drawer ── */}
      <Drawer
        open={milestoneDrawer.open}
        onClose={() => setMilestoneDrawer((p) => ({ ...p, open: false }))}
        placement="right"
        width={420}
        title={
          <div>
            <div style={{ fontWeight: 800, color: "#0b1b3a", fontSize: 15 }}>{milestoneDrawer.editingMilestoneId ? "Edit Milestone" : "Add Milestones"}</div>
            {milestoneDrawer.editingMilestoneId && milestoneDrawer.goalTitle && (
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)", fontWeight: 400, marginTop: 2 }}>Goal: {milestoneDrawer.goalTitle}</div>
            )}
          </div>
        }
        footer={
          milestoneDrawer.confirmDelete ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>Delete this milestone?</div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", lineHeight: 1.5, marginBottom: 12 }}>
                This will permanently remove the milestone. This cannot be undone.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => setMilestoneDrawer((p) => ({ ...p, confirmDelete: false }))} disabled={milestoneDrawer.saving}
                  style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "transparent", fontSize: 13, cursor: milestoneDrawer.saving ? "not-allowed" : "pointer" }}>
                  Cancel
                </button>
                <button type="button" onClick={deleteMilestone} disabled={milestoneDrawer.saving}
                  style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: milestoneDrawer.saving ? "not-allowed" : "pointer", opacity: milestoneDrawer.saving ? 0.6 : 1 }}>
                  {milestoneDrawer.saving ? "Deleting…" : "Yes, delete"}
                </button>
              </div>
            </div>
          ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div>
              {milestoneDrawer.editingMilestoneId && (
                <button type="button" onClick={confirmDeleteMilestone} disabled={milestoneDrawer.saving}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid #fca5a5", background: "transparent", color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: milestoneDrawer.saving ? "not-allowed" : "pointer" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => setMilestoneDrawer((p) => ({ ...p, open: false }))}
                style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)", background: "transparent", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
              {(() => {
                const isEdit = !!milestoneDrawer.editingMilestoneId;
                const filledCount = (milestoneDrawer.entries || []).filter((e) => e.title.trim()).length;
                const canSave = isEdit ? !!milestoneDrawer.title.trim() : (!!milestoneDrawer.goalId && filledCount > 0);
                const label = milestoneDrawer.saving
                  ? "Saving…"
                  : isEdit
                    ? "Update Milestone"
                    : filledCount > 1 ? `Save ${filledCount} Milestones` : "Save Milestone";
                return (
                  <button type="button" onClick={saveMilestone} disabled={milestoneDrawer.saving || !canSave}
                    style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: colors.brandPrimary, color: "#fff", fontSize: 13, fontWeight: 700, cursor: milestoneDrawer.saving || !canSave ? "not-allowed" : "pointer", opacity: milestoneDrawer.saving || !canSave ? 0.6 : 1 }}>
                    {label}
                  </button>
                );
              })()}
            </div>
          </div>
          )
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {milestoneDrawer.editingMilestoneId ? (
            <>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Milestone title *</label>
                <input
                  value={milestoneDrawer.title}
                  onChange={(e) => setMilestoneDrawer((p) => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Complete chapter 1"
                  autoFocus
                  style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Description</label>
                <textarea
                  value={milestoneDrawer.description}
                  onChange={(e) => setMilestoneDrawer((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Optional details…"
                  rows={3}
                  style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Target date</label>
                <input
                  type="date"
                  value={milestoneDrawer.date}
                  onChange={(e) => setMilestoneDrawer((p) => ({ ...p, date: e.target.value }))}
                  style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>Goal *</label>
                <select
                  value={milestoneDrawer.goalId || ""}
                  onChange={(e) => { const g = sortedGoals.find((x) => x.id === e.target.value); setMilestoneDrawer((p) => ({ ...p, goalId: g?.id ?? null, goalTitle: g?.title ?? "" })); }}
                  style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 10px", fontSize: 13, outline: "none", boxSizing: "border-box", background: "#fff" }}
                >
                  {!milestoneDrawer.goalId && <option value="">Select a goal…</option>}
                  {sortedGoals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select>
              </div>
              {(milestoneDrawer.entries || []).map((entry, idx) => (
                <div key={idx} style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,0.5)" }}>Milestone {idx + 1}</div>
                    {milestoneDrawer.entries.length > 1 && (
                      <button type="button" title="Remove" onClick={() => setMilestoneDrawer((p) => ({ ...p, entries: p.entries.filter((_, i) => i !== idx) }))}
                        style={{ border: "none", background: "transparent", cursor: "pointer", color: "#dc2626", display: "inline-flex", alignItems: "center", padding: 2 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>
                      </button>
                    )}
                  </div>
                  <input
                    value={entry.title}
                    onChange={(e) => { const v = e.target.value; setMilestoneDrawer((p) => ({ ...p, entries: p.entries.map((en, i) => i === idx ? { ...en, title: v } : en) })); }}
                    placeholder="Milestone title *"
                    autoFocus={idx === 0}
                    style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                  <textarea
                    value={entry.description}
                    onChange={(e) => { const v = e.target.value; setMilestoneDrawer((p) => ({ ...p, entries: p.entries.map((en, i) => i === idx ? { ...en, description: v } : en) })); }}
                    placeholder="Optional details…"
                    rows={2}
                    style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                  <input
                    type="date"
                    value={entry.date}
                    onChange={(e) => { const v = e.target.value; setMilestoneDrawer((p) => ({ ...p, entries: p.entries.map((en, i) => i === idx ? { ...en, date: v } : en) })); }}
                    style={{ width: "100%", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  />
                </div>
              ))}
              <button type="button"
                onClick={() => setMilestoneDrawer((p) => ({ ...p, entries: [...p.entries, emptyMilestoneEntry()] }))}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, border: `1px dashed ${colors.brandPrimary}`, background: "transparent", color: colors.brandPrimary, borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/><line x1="10" y1="5" x2="10" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="5" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Add another milestone
              </button>
            </>
          )}
        </div>
      </Drawer>

      {/* ── Delete zone — hidden for locked dreams ── */}
      {!isDreamLocked && <div
        style={{
          marginTop: 48,
          paddingTop: 24,
          borderTop: `1px solid ${colors.borderLight}`,
        }}
      >
        {!showDeleteConfirm ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              appearance: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              border: "1px solid #fca5a5",
              background: "#fff5f5",
              color: "#dc2626",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
            Delete this dream
          </button>
        ) : (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #fca5a5",
              borderRadius: 12,
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>Delete this dream?</div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", lineHeight: 1.5 }}>
                This will permanently remove the dream, all goals, milestones and progress. This cannot be undone.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  appearance: "none",
                  border: `1px solid ${colors.borderLight}`,
                  background: "#fff",
                  color: "#374151",
                  borderRadius: 8,
                  padding: "6px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  appearance: "none",
                  border: "none",
                  background: deleting ? "#ef4444aa" : "#dc2626",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "6px 16px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: deleting ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        )}
      </div>}

      <UpgradeModal open={showUpgrade} trigger={upgradeTrigger} onClose={() => setShowUpgrade(false)} />
    </div>
  );
}
